import { useState, useEffect, useRef, useCallback, Fragment, KeyboardEvent, ChangeEvent } from 'react';
import toast from 'react-hot-toast';
import { api } from '../../api/client';

// Attachment type for pending uploads
interface PendingAttachment {
  file: File;
  previewUrl?: string;
  uploading: boolean;
  error?: string;
}

// Types
export interface ReplyTo {
  id: string;
  content: string;
  senderName: string;
}

export interface ChatMessage {
  id: string;
  senderId: string;
  senderName: string;
  content: string;
  mentions?: Array<{ userId: string; userName: string }>;
  attachments?: Array<{ id?: string; name: string; url: string; type?: string }>;
  replyToId?: string;
  replyTo?: ReplyTo | null;
  readBy?: string[];
  createdAt: string;
}

export interface MentionUser {
  id: string;
  name: string;
  email: string;
}

export interface ChatBoxProps {
  entityType: 'request' | 'construction' | 'talkroom';
  entityId: string;
  currentUserId: string;
  title?: string;
  className?: string;
  hideFileUpload?: boolean;
}

// Helper function to normalize @ character (full-width to half-width)
const normalizeAtSymbol = (text: string): string => {
  return text.replace(/＠/g, '@');
};

// Helper function to truncate reply content
const truncateReplyContent = (content: string, maxLength: number = 15): string => {
  if (!content) return '';
  if (content.length <= maxLength) return content;
  return content.substring(0, maxLength) + '...';
};

// Parse message content and find mentions
const parseMentions = (content: string, users: MentionUser[]): Array<{ userId: string; userName: string }> => {
  const normalized = normalizeAtSymbol(content);
  const mentionRegex = /@([^\s@]+)/g;
  const mentions: Array<{ userId: string; userName: string }> = [];
  let match;
  
  while ((match = mentionRegex.exec(normalized)) !== null) {
    const userName = match[1];
    const user = users.find(u => u.name === userName);
    if (user) {
      mentions.push({ userId: user.id, userName: user.name });
    }
  }
  
  return mentions;
};

// Render message content with mention highlighting
interface MentionTooltipData {
  user: MentionUser | undefined;
  x: number;
  y: number;
}

export default function ChatBox({
  entityType,
  entityId,
  currentUserId,
  title = 'チャット',
  className = '',
  hideFileUpload = false,
}: ChatBoxProps) {
  // State
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [newMessage, setNewMessage] = useState(() => {
    try {
      return sessionStorage.getItem(`chat-draft-${entityType}-${entityId}`) || '';
    } catch { return ''; }
  });
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const [users, setUsers] = useState<MentionUser[]>([]);
  
  // Mention state
  const [showMentionList, setShowMentionList] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionStartPos, setMentionStartPos] = useState(0);
  const [selectedMentionIndex, setSelectedMentionIndex] = useState(0);
  const [mentionTooltip, setMentionTooltip] = useState<MentionTooltipData | null>(null);
  
  // Connection state
  const [connectionError, setConnectionError] = useState(false);
  const pollFailCountRef = useRef(0);

  // File attachment state
  const [pendingAttachments, setPendingAttachments] = useState<PendingAttachment[]>([]);
  
  // Reply state
  const [replyingTo, setReplyingTo] = useState<ChatMessage | null>(null);
  
  // Image preview popup state
  const [previewImage, setPreviewImage] = useState<{ url: string; name: string } | null>(null);
  
  // Refs
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const mentionListRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const formRef = useRef<HTMLFormElement>(null);

  const INITIAL_LIMIT = 10;
  const LOAD_MORE_LIMIT = 30;
  
  // 読み込み中フラグ（無限スクロール用）
  const [loadingMore, setLoadingMore] = useState(false);

  // Build API endpoint based on entity type
  const getApiEndpoint = useCallback(() => {
    switch (entityType) {
      case 'request':
        return `/api/requests/${entityId}/messages`;
      case 'construction':
        return `/api/constructions/${entityId}/messages`;
      case 'talkroom':
        return `/api/talkrooms/${entityId}/messages`;
    }
  }, [entityType, entityId]);

  // Fetch users for mentions (talkroom: room members, otherwise: all active users)
  const fetchUsers = useCallback(async () => {
    try {
      if (entityType === 'talkroom') {
        const data = await api.get<{ items?: MentionUser[]; members?: Array<{ userId: string; userName: string; user?: MentionUser }> }>(
          `/api/talkrooms/${entityId}/members`
        );
        if (Array.isArray(data?.members)) {
          setUsers(data.members.map(m => m.user ?? { id: m.userId, name: m.userName, email: '' }));
        } else if (Array.isArray(data?.items)) {
          setUsers(data.items);
        }
      } else {
        const data = await api.get<{ items: MentionUser[] }>('/api/users?status=active');
        setUsers(Array.isArray(data?.items) ? data.items : []);
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  }, [entityType, entityId]);

  // Use ref to track offset for loadMore without causing re-renders
  const offsetRef = useRef(offset);
  offsetRef.current = offset;

  // Refs to avoid stale closures in polling
  const messagesRef = useRef<ChatMessage[]>(messages);
  messagesRef.current = messages;
  const loadingRef = useRef(loading);
  loadingRef.current = loading;
  const sendingRef = useRef(sending);
  sendingRef.current = sending;
  const loadingMoreRef = useRef(loadingMore);
  loadingMoreRef.current = loadingMore;
  const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isTabVisibleRef = useRef(true);

  // Scroll to bottom without triggering page scroll
  const scrollToBottom = useCallback(() => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  }, []);

  // AbortController for cancelling in-flight requests on entityId change
  const abortControllerRef = useRef<AbortController | null>(null);

  // Fetch messages with pagination
  const fetchMessages = useCallback(async (loadMore = false) => {
    if (loadMore) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }
    
    try {
      const currentOffset = loadMore ? offsetRef.current : 0;
      const limit = loadMore ? LOAD_MORE_LIMIT : INITIAL_LIMIT;
      const endpoint = `${getApiEndpoint()}?limit=${limit}&offset=${currentOffset}`;
      
      const data = await api.get<{ items: ChatMessage[]; hasMore?: boolean; total?: number }>(endpoint);
      const items = Array.isArray(data?.items) ? data.items : [];
      
      if (loadMore) {
        // スクロール位置を保持するため、現在の高さを記録
        const container = messagesContainerRef.current;
        const prevScrollHeight = container?.scrollHeight || 0;
        
        setMessages(prev => [...items, ...prev]);
        setOffset(currentOffset + items.length);
        
        // 新しいメッセージ追加後、スクロール位置を調整
        requestAnimationFrame(() => {
          if (container) {
            const newScrollHeight = container.scrollHeight;
            container.scrollTop = newScrollHeight - prevScrollHeight;
          }
        });
      } else {
        setMessages(items);
        setOffset(items.length);
        setTimeout(() => scrollToBottom(), 50);

        if (entityType === 'talkroom' && items.length > 0) {
          const unreadIds = items.filter(m => m.senderId !== currentUserId && !(m.readBy || []).includes(currentUserId)).map(m => m.id);
          if (unreadIds.length > 0) {
            api.post(`/api/talkrooms/${entityId}/messages/read`, { messageIds: unreadIds }).catch(() => {});
          }
        }
      }
      
      const hasMoreMessages = data?.hasMore ?? (items.length === limit);
      setHasMore(hasMoreMessages);
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [getApiEndpoint, scrollToBottom]);

  // 上部スクロール時に過去メッセージを読み込む
  const handleScroll = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container || loadingMore || !hasMore) return;
    
    // 上部に近づいたら（50px以内）過去データを読み込む
    if (container.scrollTop < 50) {
      fetchMessages(true);
    }
  }, [loadingMore, hasMore, fetchMessages]);

  // Initial load - abort previous requests on entityId change
  useEffect(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    setOffset(0);
    setMessages([]);
    setHasMore(false);
    setNewMessage('');
    setPendingAttachments(prev => {
      prev.forEach(att => { if (att.previewUrl) URL.revokeObjectURL(att.previewUrl); });
      return [];
    });
    setReplyingTo(null);
    fetchMessages();
    fetchUsers();
    let focusTimer: ReturnType<typeof setTimeout> | undefined;
    if (window.innerWidth >= 1024) {
      focusTimer = setTimeout(() => textareaRef.current?.focus(), 100);
    }
    return () => {
      if (focusTimer) clearTimeout(focusTimer);
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId, fetchMessages, fetchUsers]);

  // リアルタイム更新のためのポーリング（3秒間隔）+ タブ非表示時は停止
  useEffect(() => {
    const startPolling = () => {
      if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = setInterval(async () => {
        if (loadingRef.current || sendingRef.current || loadingMoreRef.current) return;
        if (!isTabVisibleRef.current) return;

        try {
          const data = await api.get<{
            items: ChatMessage[];
            hasMore?: boolean;
            total?: number;
          }>(`${getApiEndpoint()}?limit=10&offset=0`);

          const newItems = data?.items || [];
          if (newItems.length > 0) {
            const currentMessages = messagesRef.current;
            const latestNewId = newItems[newItems.length - 1]?.id;
            const latestCurrentId = currentMessages[currentMessages.length - 1]?.id;

            if (latestNewId && latestNewId !== latestCurrentId) {
              setMessages(prev => {
                const existingIds = new Set(prev.map(m => m.id));
                const uniqueNewMessages = newItems.filter(m => !existingIds.has(m.id));

                if (uniqueNewMessages.length > 0) {
                  setTimeout(() => scrollToBottom(), 50);
                  return [...prev, ...uniqueNewMessages];
                }
                return prev;
              });
            }
          }
          pollFailCountRef.current = 0;
          if (connectionError) setConnectionError(false);
        } catch (error) {
          console.debug('Polling error:', error);
          pollFailCountRef.current++;
          if (pollFailCountRef.current >= 3) {
            setConnectionError(true);
          }
        }
      }, 3000);
    };

    const handleVisibility = () => {
      isTabVisibleRef.current = document.visibilityState === 'visible';
      if (document.visibilityState === 'visible') {
        startPolling();
      } else {
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
      }
    };

    startPolling();
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [entityId, getApiEndpoint, scrollToBottom]);

  // スクロールイベントリスナー
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    
    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  // コンテンツがコンテナを埋めない場合に自動でloadMore
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container || loading || loadingMore || !hasMore) return;
    if (container.scrollHeight <= container.clientHeight) {
      fetchMessages(true);
    }
  }, [messages.length, hasMore, loading, loadingMore, fetchMessages]);

  // Filter users for mention dropdown
  const filteredMentionUsers = useCallback(() => {
    if (!mentionQuery) {
      return users.filter(u => u.id !== currentUserId).slice(0, 10);
    }
    
    const query = mentionQuery.toLowerCase();
    const prefixMatches: MentionUser[] = [];
    const partialMatches: MentionUser[] = [];
    
    users.forEach(user => {
      if (user.id === currentUserId) return;
      const name = user.name.toLowerCase();
      if (name.startsWith(query)) {
        prefixMatches.push(user);
      } else if (name.includes(query)) {
        partialMatches.push(user);
      }
    });
    
    return [...prefixMatches, ...partialMatches].slice(0, 10);
  }, [users, mentionQuery, currentUserId]);

  // Handle text input change with auto-resize
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    const cursorPos = e.target.selectionStart || 0;
    
    setNewMessage(value);
    try { sessionStorage.setItem(`chat-draft-${entityType}-${entityId}`, value); } catch {};

    e.target.style.height = 'auto';
    e.target.style.height = `${Math.min(e.target.scrollHeight, 200)}px`;
    
    const textBeforeCursor = value.slice(0, cursorPos);
    const normalizedText = normalizeAtSymbol(textBeforeCursor);
    const lastAtIndex = normalizedText.lastIndexOf('@');
    
    if (lastAtIndex !== -1) {
      const charBefore = lastAtIndex > 0 ? normalizedText[lastAtIndex - 1] : ' ';
      if (charBefore === ' ' || charBefore === '\n' || lastAtIndex === 0) {
        const query = normalizedText.slice(lastAtIndex + 1);
        if (!query.includes(' ') && !query.includes('\n')) {
          setMentionQuery(query);
          setMentionStartPos(lastAtIndex);
          setShowMentionList(true);
          setSelectedMentionIndex(0);
          return;
        }
      }
    }
    
    setShowMentionList(false);
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    // Ctrl+Enterで送信
    if (e.key === 'Enter' && e.ctrlKey) {
      e.preventDefault();
      if (sending) return;
      formRef.current?.requestSubmit();
      return;
    }
    
    if (!showMentionList) return;
    
    const filtered = filteredMentionUsers();
    
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedMentionIndex(prev => {
          const newIndex = prev < filtered.length - 1 ? prev + 1 : 0;
          scrollToMentionItem(newIndex);
          return newIndex;
        });
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedMentionIndex(prev => {
          const newIndex = prev > 0 ? prev - 1 : filtered.length - 1;
          scrollToMentionItem(newIndex);
          return newIndex;
        });
        break;
      case 'Enter':
        if (filtered.length > 0) {
          e.preventDefault();
          handleMentionSelect(filtered[selectedMentionIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setShowMentionList(false);
        break;
    }
  };

  const scrollToMentionItem = (index: number) => {
    requestAnimationFrame(() => {
      const list = mentionListRef.current;
      if (list) {
        const item = list.children[index] as HTMLElement;
        if (item) {
          item.scrollIntoView({ block: 'nearest' });
        }
      }
    });
  };

  const handleMentionSelect = (user: MentionUser) => {
    const normalized = normalizeAtSymbol(newMessage);
    const beforeMention = normalized.slice(0, mentionStartPos);
    const afterMention = normalized.slice(mentionStartPos + mentionQuery.length + 1);
    
    const newText = `${beforeMention}@${user.name} ${afterMention}`;
    setNewMessage(newText);
    setShowMentionList(false);
    textareaRef.current?.focus();
  };

  // Handle file selection
  const handleFileSelect = (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    const newAttachments: PendingAttachment[] = Array.from(files).map(file => {
      const isImage = file.type.startsWith('image/');
      return {
        file,
        previewUrl: isImage ? URL.createObjectURL(file) : undefined,
        uploading: false,
      };
    });
    
    setPendingAttachments(prev => [...prev, ...newAttachments]);
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Remove pending attachment
  const handleRemoveAttachment = (index: number) => {
    setPendingAttachments(prev => {
      const removed = prev[index];
      if (removed.previewUrl) {
        URL.revokeObjectURL(removed.previewUrl);
      }
      return prev.filter((_, i) => i !== index);
    });
  };

  // Upload files to R2
  const uploadAttachments = async (): Promise<Array<{ name: string; url: string; type: string }>> => {
    const uploadedAttachments: Array<{ name: string; url: string; type: string }> = [];
    
    for (let i = 0; i < pendingAttachments.length; i++) {
      const attachment = pendingAttachments[i];
      
      setPendingAttachments(prev => 
        prev.map((att, idx) => idx === i ? { ...att, uploading: true, error: undefined } : att)
      );
      
      try {
        const url = await api.uploadFile(attachment.file, {
          entityType,
          entityId,
          folder: 'chat-attachments',
        });
        
        uploadedAttachments.push({
          name: attachment.file.name,
          url,
          type: attachment.file.type,
        });
        
        setPendingAttachments(prev => 
          prev.map((att, idx) => idx === i ? { ...att, uploading: false } : att)
        );
      } catch (error) {
        console.error('Failed to upload file:', error);
        const errorMessage = error instanceof Error ? error.message : 'アップロード失敗';
        setPendingAttachments(prev => 
          prev.map((att, idx) => idx === i ? { ...att, uploading: false, error: errorMessage } : att)
        );
        throw error;
      }
    }
    
    return uploadedAttachments;
  };

  // Handle message send
  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (sending) return;
    const trimmedMessage = newMessage.trim();
    const hasAttachments = pendingAttachments.length > 0;
    
    // メッセージまたは添付ファイルがない場合は送信しない
    if (!trimmedMessage && !hasAttachments) return;
    
    setSending(true);
    try {
      // 添付ファイルをアップロード
      let uploadedAttachments: Array<{ name: string; url: string; type: string }> = [];
      if (hasAttachments) {
        uploadedAttachments = await uploadAttachments();
      }
      
      const mentions = trimmedMessage ? parseMentions(trimmedMessage, users) : [];
      
      const messageContent = trimmedMessage ? normalizeAtSymbol(trimmedMessage) : '📎 ファイルを添付しました';
      const savedReplyingTo = replyingTo;

      const result = await api.post<ChatMessage>(getApiEndpoint(), {
        content: messageContent,
        mentions: mentions.length > 0 ? mentions : null,
        attachments: uploadedAttachments.length > 0 ? uploadedAttachments : null,
        replyToId: savedReplyingTo?.id || null,
      });
      
      // クリーンアップ
      pendingAttachments.forEach(att => {
        if (att.previewUrl) {
          URL.revokeObjectURL(att.previewUrl);
        }
      });
      setPendingAttachments([]);
      setReplyingTo(null);
      setNewMessage('');
      try { sessionStorage.removeItem(`chat-draft-${entityType}-${entityId}`); } catch {};
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }

      // 楽観的更新: サーバーレスポンスがあればそれを、なければ仮メッセージを追加
      const optimisticMsg: ChatMessage = result && (result as any).id
        ? result
        : {
            id: `temp-${Date.now()}`,
            senderId: currentUserId,
            senderName: users.find(u => u.id === currentUserId)?.name || '',
            content: messageContent,
            mentions: mentions.length > 0 ? mentions : undefined,
            attachments: uploadedAttachments.length > 0 ? uploadedAttachments : undefined,
            replyToId: savedReplyingTo?.id,
            replyTo: savedReplyingTo ? { id: savedReplyingTo.id, content: savedReplyingTo.content, senderName: savedReplyingTo.senderName } : undefined,
            createdAt: new Date().toISOString(),
          };

      setMessages(prev => [...prev, optimisticMsg]);

      if (result && (result as any).warning) {
        toast((result as any).warning, { icon: '⚠️', style: { background: '#fef3c7', color: '#92400e' } });
      }

      setTimeout(() => {
        scrollToBottom();
        if (window.innerWidth >= 1024) {
          textareaRef.current?.focus();
        }
      }, 100);
    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error('メッセージの送信に失敗しました');
    } finally {
      setSending(false);
    }
  };

  // メッセージ削除（送信後5分以内、自分のメッセージのみ）
  const canDeleteMessage = (msg: ChatMessage): boolean => {
    if (msg.senderId !== currentUserId) return false;
    const fiveMinutes = 5 * 60 * 1000;
    return new Date().getTime() - new Date(msg.createdAt).getTime() < fiveMinutes;
  };

  const handleDeleteMessage = async (msg: ChatMessage) => {
    if (!canDeleteMessage(msg)) return;
    if (entityType !== 'talkroom') return;
    try {
      await api.delete(`/api/talkrooms/${entityId}/messages/${msg.id}`);
      setMessages(prev => prev.filter(m => m.id !== msg.id));
    } catch (error) {
      console.error('Failed to delete message:', error);
    }
  };

  // 返信を選択してフォーカス
  const handleReply = (msg: ChatMessage) => {
    setReplyingTo(msg);
    if (window.innerWidth >= 1024) {
      setTimeout(() => textareaRef.current?.focus(), 50);
    }
  };

  const handleMentionMouseEnter = (
    e: React.MouseEvent<HTMLSpanElement>,
    userName: string
  ) => {
    const user = users.find(u => u.name === userName);
    const rect = e.currentTarget.getBoundingClientRect();
    setMentionTooltip({
      user,
      x: rect.left,
      y: rect.bottom + 4,
    });
  };

  const handleMentionMouseLeave = () => {
    setMentionTooltip(null);
  };

  // 返信元メッセージにスクロール（必要に応じて過去データを取得）
  const scrollToMessage = useCallback(async (messageId: string) => {
    // まず現在のリストにメッセージがあるか確認
    const existingElement = document.getElementById(`msg-${messageId}`);
    if (existingElement) {
      existingElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // ハイライトアニメーション
      existingElement.classList.add('bg-yellow-100', 'dark:bg-yellow-900/30');
      setTimeout(() => {
        existingElement.classList.remove('bg-yellow-100', 'dark:bg-yellow-900/30');
      }, 1500);
      return;
    }
    
    // メッセージがリストにない場合、過去データを取得
    if (!hasMore) return;
    
    setLoadingMore(true);
    try {
      // 対象メッセージが見つかるまで過去データを取得（最大300件まで）
      let currentOffset = offsetRef.current;
      let found = false;
      let allNewMessages: ChatMessage[] = [];
      
      for (let i = 0; i < 10 && !found; i++) {
        const endpoint = `${getApiEndpoint()}?limit=${LOAD_MORE_LIMIT}&offset=${currentOffset}`;
        const data = await api.get<{ items: ChatMessage[]; hasMore?: boolean }>(endpoint);
        const items = Array.isArray(data?.items) ? data.items : [];
        
        if (items.length === 0) break;
        
        allNewMessages = [...items, ...allNewMessages];
        currentOffset += items.length;
        
        // 対象メッセージが含まれているか確認
        if (items.some(m => m.id === messageId)) {
          found = true;
        }
        
        if (!data?.hasMore) break;
      }
      
      if (allNewMessages.length > 0) {
        setMessages(prev => [...allNewMessages, ...prev]);
        setOffset(currentOffset);
        setHasMore(found ? hasMore : false);
        
        // DOMが更新されるのを待ってからスクロール
        setTimeout(() => {
          const element = document.getElementById(`msg-${messageId}`);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            element.classList.add('bg-yellow-100', 'dark:bg-yellow-900/30');
            setTimeout(() => {
              element.classList.remove('bg-yellow-100', 'dark:bg-yellow-900/30');
            }, 1500);
          }
        }, 100);
      }
    } catch (error) {
      console.error('Failed to load messages for scroll:', error);
    } finally {
      setLoadingMore(false);
    }
  }, [hasMore, getApiEndpoint]);

  // Render text with clickable URLs
  const renderTextWithLinks = (text: string, keyPrefix: string) => {
    const segments = text.split(/(https?:\/\/[^\s<]+)/g);
    if (segments.length === 1) return text;
    return segments.map((seg, j) =>
      /^https?:\/\//.test(seg) ? (
        <a
          key={`${keyPrefix}-${j}`}
          href={seg}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-500 underline break-all"
        >
          {seg}
        </a>
      ) : (
        <Fragment key={`${keyPrefix}-${j}`}>{seg}</Fragment>
      )
    );
  };

  // Render message content with highlighted mentions and clickable URLs
  const renderMessageContent = (content: string, isOwn: boolean) => {
    const normalized = normalizeAtSymbol(content);
    const parts = normalized.split(/(@[^\s@]+)/g);
    
    return parts.map((part, i) => {
      if (part.startsWith('@')) {
        const userName = part.slice(1);
        return (
          <span
            key={i}
            className={`font-semibold cursor-pointer ${
              isOwn ? 'text-green-700' : 'text-blue-600 dark:text-blue-400'
            } hover:underline`}
            onMouseEnter={(e) => handleMentionMouseEnter(e, userName)}
            onMouseLeave={handleMentionMouseLeave}
          >
            {part}
          </span>
        );
      }
      return <Fragment key={i}>{renderTextWithLinks(part, `t${i}`)}</Fragment>;
    });
  };

  // Check if same sender as previous (reset when date changes)
  const isSameSenderAsPrevious = (index: number): boolean => {
    if (index === 0) return false;
    const prev = new Date(messages[index - 1].createdAt);
    const curr = new Date(messages[index].createdAt);
    if (prev.getFullYear() !== curr.getFullYear() || prev.getMonth() !== curr.getMonth() || prev.getDate() !== curr.getDate()) return false;
    return messages[index].senderId === messages[index - 1].senderId;
  };

  // Check if date changed from previous message
  const shouldShowDateSeparator = (index: number): boolean => {
    if (index === 0) return true;
    const prev = new Date(messages[index - 1].createdAt);
    const curr = new Date(messages[index].createdAt);
    return prev.getFullYear() !== curr.getFullYear() || prev.getMonth() !== curr.getMonth() || prev.getDate() !== curr.getDate();
  };

  const formatDateLabel = (dateString: string): string => {
    const d = new Date(dateString);
    const now = new Date();
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    if (d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate()) return '今日';
    if (d.getFullYear() === yesterday.getFullYear() && d.getMonth() === yesterday.getMonth() && d.getDate() === yesterday.getDate()) return '昨日';
    const weekDays = ['日', '月', '火', '水', '木', '金', '土'];
    return `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}(${weekDays[d.getDay()]})`;
  };

  // Format time
  const formatTime = (dateString: string): string => {
    const date = new Date(dateString);
    const hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const period = hours < 12 ? '午前' : '午後';
    const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
    return `${period} ${displayHours}:${minutes}`;
  };

  // ファイルが画像かどうか判定
  const isImageFile = (url: string, type?: string): boolean => {
    if (type?.startsWith('image/')) return true;
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.svg'];
    const lowerUrl = url.toLowerCase();
    return imageExtensions.some(ext => lowerUrl.includes(ext));
  };

  // Get avatar color based on name
  const getAvatarColor = (name: string): string => {
    const colors = [
      'bg-red-500', 'bg-orange-500', 'bg-amber-500', 'bg-yellow-500',
      'bg-lime-500', 'bg-green-500', 'bg-emerald-500', 'bg-teal-500',
      'bg-cyan-500', 'bg-sky-500', 'bg-blue-500', 'bg-indigo-500',
      'bg-violet-500', 'bg-purple-500', 'bg-fuchsia-500', 'bg-pink-500',
    ];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  const filtered = filteredMentionUsers();

  return (
    <div className={`flex flex-col h-full overflow-hidden bg-white dark:bg-gray-900 max-w-full ${className}`}>
      {/* Header - Blue theme */}
      {title && (
        <div className="flex items-center px-4 py-3 bg-blue-600 text-white flex-shrink-0">
          <h3 className="font-semibold text-base">{title}</h3>
        </div>
      )}
      
      {/* Connection error banner */}
      {connectionError && (
        <div className="px-4 py-2 bg-yellow-50 dark:bg-yellow-900/30 border-b border-yellow-200 dark:border-yellow-800 text-yellow-800 dark:text-yellow-200 text-sm flex items-center gap-2 flex-shrink-0">
          <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
          接続に問題があります。自動的に再接続を試みています...
        </div>
      )}

      {/* Messages container */}
      <div
        ref={messagesContainerRef}
        className="flex-1 overflow-y-auto overflow-x-hidden px-3 py-2 bg-white dark:bg-gray-900"
        style={{ overscrollBehavior: 'contain' }}
      >
        {/* 上部読み込み中インジケータ */}
        {loadingMore && (
          <div className="text-center py-3 mb-2">
            <div className="inline-flex items-center gap-2 text-sm text-gray-500">
              <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
              <span>過去のメッセージを読み込み中...</span>
            </div>
          </div>
        )}
        
        {/* 過去メッセージがある場合のヒント */}
        {hasMore && !loadingMore && (
          <div className="text-center py-2 mb-2">
            <p className="text-xs text-gray-400">↑ スクロールして過去のメッセージを表示</p>
          </div>
        )}
        
        {/* Messages */}
        {messages.length === 0 && !loading ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-400">
            <svg className="w-12 h-12 mb-3 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            <p className="text-sm">メッセージはありません</p>
          </div>
        ) : (
          <div className="space-y-1">
            {messages.map((msg, index) => {
              const isOwn = msg.senderId === currentUserId;
              const sameSender = isSameSenderAsPrevious(index);
              const senderName = msg.senderName || '名前なし';
              const showDate = shouldShowDateSeparator(index);
              
              // 自分のメッセージは右寄せ、他のユーザーは左寄せ
              if (isOwn) {
                return (
                  <Fragment key={msg.id}>
                    {showDate && (
                      <div className="flex items-center gap-3 my-3">
                        <div className="flex-1 border-t border-gray-200 dark:border-gray-700" />
                        <span className="text-xs text-gray-400 whitespace-nowrap">{formatDateLabel(msg.createdAt)}</span>
                        <div className="flex-1 border-t border-gray-200 dark:border-gray-700" />
                      </div>
                    )}
                  <div 
                    id={`msg-${msg.id}`}
                    className={`flex justify-end ${sameSender ? 'mt-0.5' : 'mt-4'} group transition-colors duration-500 overflow-hidden`}
                  >
                    <div className="w-full min-w-0 flex flex-col items-end overflow-hidden">
                      {/* Message bubble with reply button */}
                      <div className="flex items-end gap-1 w-full justify-end">
                        {/* Delete button (own messages, within 5 min) */}
                        {canDeleteMessage(msg) && entityType === 'talkroom' && (
                          <button
                            type="button"
                            onClick={() => handleDeleteMessage(msg)}
                            className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-red-500 transition-opacity flex-shrink-0"
                            title="削除"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        )}
                        {/* Reply button */}
                        <button
                          type="button"
                          onClick={() => handleReply(msg)}
                          className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-opacity flex-shrink-0"
                          title="返信"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                          </svg>
                        </button>
                        {entityType === 'talkroom' && msg.readBy && msg.readBy.filter(id => id !== currentUserId).length > 0 && (
                          <span className="text-[10px] text-gray-400 flex-shrink-0 pb-1">既読</span>
                        )}
                        <span className="text-[10px] text-gray-400 flex-shrink-0 pb-1">
                          {formatTime(msg.createdAt)}
                        </span>
                        <div className="rounded-2xl rounded-br-md px-3 py-2 bg-green-100 dark:bg-green-900/40 text-gray-900 dark:text-white overflow-hidden break-words">
                          {/* Reply reference inside bubble */}
                          {msg.replyTo && (
                            <button
                              type="button"
                              onClick={() => scrollToMessage(msg.replyTo!.id)}
                              className="flex items-start gap-1.5 mb-2 pb-2 border-b border-green-200 dark:border-green-700/50 w-full text-left hover:bg-green-200/50 dark:hover:bg-green-800/30 -mx-2 px-2 pt-1 -mt-1 rounded-t-xl transition-colors"
                            >
                              <div className="w-0.5 h-full min-h-[24px] bg-green-400 dark:bg-green-600 rounded-full flex-shrink-0" />
                              <div className="min-w-0 flex-1 overflow-hidden">
                                <p className="text-[10px] text-gray-500 dark:text-gray-400">{truncateReplyContent(msg.replyTo.content)}</p>
                              </div>
                            </button>
                          )}
                          <p className="text-[14px] leading-relaxed whitespace-pre-wrap break-all overflow-hidden">
                            {renderMessageContent(msg.content, isOwn)}
                          </p>
                        </div>
                      </div>
                      
                      {/* Attachments */}
                      {msg.attachments && msg.attachments.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-2 justify-end">
                          {msg.attachments.map((att, i) => (
                            isImageFile(att.url, att.type) ? (
                              // 画像ファイル: サムネイル表示
                              <button
                                key={i}
                                type="button"
                                onClick={() => setPreviewImage({ url: att.url, name: att.name })}
                                className="relative group rounded-lg overflow-hidden hover:opacity-90 transition-opacity"
                              >
                                <img
                                  src={att.url}
                                  alt={att.name}
                                  className="max-w-[200px] max-h-[150px] object-cover rounded-lg"
                                />
                                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                                  <svg className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-lg" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                                  </svg>
                                </div>
                              </button>
                            ) : (
                              // その他のファイル: ダウンロードリンク
                              <a
                                key={i}
                                href={att.url}
                                download={att.name}
                                className="inline-flex items-center gap-1.5 text-xs bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 px-3 py-2 rounded-lg hover:bg-green-100 dark:hover:bg-green-900/30"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                <span className="truncate max-w-[150px]">{att.name}</span>
                              </a>
                            )
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  </Fragment>
                );
              }
              
              // 他のユーザーのメッセージは左寄せ
              return (
                <Fragment key={msg.id}>
                  {showDate && (
                    <div className="flex items-center gap-3 my-3">
                      <div className="flex-1 border-t border-gray-200 dark:border-gray-700" />
                      <span className="text-xs text-gray-400 whitespace-nowrap">{formatDateLabel(msg.createdAt)}</span>
                      <div className="flex-1 border-t border-gray-200 dark:border-gray-700" />
                    </div>
                  )}
                <div 
                  id={`msg-${msg.id}`}
                  className={`flex gap-1.5 ${sameSender ? 'mt-0.5' : 'mt-4'} group transition-colors duration-500 overflow-hidden`}
                >
                  {/* Avatar */}
                  <div className="flex-shrink-0 w-7">
                    {!sameSender && (
                      <div className={`w-7 h-7 ${getAvatarColor(senderName)} rounded-full flex items-center justify-center text-white text-xs font-medium`}>
                        {senderName.charAt(0)}
                      </div>
                    )}
                  </div>
                  
                  {/* Message content */}
                  <div className="min-w-0 flex-1 overflow-hidden">
                    {/* Sender name and time */}
                    {!sameSender && (
                      <div className="flex items-baseline gap-2 mb-1">
                        <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
                          {senderName}
                        </span>
                        <span className="text-[10px] text-gray-400 flex-shrink-0">
                          {formatTime(msg.createdAt)}
                        </span>
                      </div>
                    )}
                    
                    {/* Message bubble with reply button */}
                    <div className="flex items-end gap-1">
                      <div className="rounded-2xl rounded-bl-md px-3 py-2 bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white overflow-hidden break-words">
                        {/* Reply reference inside bubble */}
                        {msg.replyTo && (
                          <button
                            type="button"
                            onClick={() => scrollToMessage(msg.replyTo!.id)}
                            className="flex items-start gap-1.5 mb-2 pb-2 border-b border-gray-200 dark:border-gray-700 w-full text-left hover:bg-gray-200/50 dark:hover:bg-gray-700/50 -mx-2 px-2 pt-1 -mt-1 rounded-t-xl transition-colors"
                          >
                            <div className="w-0.5 h-full min-h-[24px] bg-blue-400 dark:bg-blue-500 rounded-full flex-shrink-0" />
                            <div className="min-w-0 flex-1 overflow-hidden">
                              <p className="text-[10px] text-gray-500 dark:text-gray-400">{truncateReplyContent(msg.replyTo.content)}</p>
                            </div>
                          </button>
                        )}
                        <p className="text-[14px] leading-relaxed whitespace-pre-wrap break-all overflow-hidden">
                          {renderMessageContent(msg.content, isOwn)}
                        </p>
                      </div>
                      
                      {/* Time for same sender messages */}
                      {sameSender && (
                        <span className="text-[10px] text-gray-400 flex-shrink-0 pb-1">
                          {formatTime(msg.createdAt)}
                        </span>
                      )}
                      
                      {/* Reply button */}
                      <button
                        type="button"
                        onClick={() => handleReply(msg)}
                        className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-opacity"
                        title="返信"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                        </svg>
                      </button>
                    </div>
                    
                    {/* Attachments */}
                    {msg.attachments && msg.attachments.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {msg.attachments.map((att, i) => (
                          isImageFile(att.url, att.type) ? (
                            // 画像ファイル: サムネイル表示
                            <button
                              key={i}
                              type="button"
                              onClick={() => setPreviewImage({ url: att.url, name: att.name })}
                              className="relative group rounded-lg overflow-hidden hover:opacity-90 transition-opacity"
                            >
                              <img
                                src={att.url}
                                alt={att.name}
                                className="max-w-[200px] max-h-[150px] object-cover rounded-lg"
                              />
                              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                                <svg className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-lg" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                                </svg>
                              </div>
                            </button>
                          ) : (
                            // その他のファイル: ダウンロードリンク
                            <a
                              key={i}
                              href={att.url}
                              download={att.name}
                              className="inline-flex items-center gap-1.5 text-xs bg-gray-100 dark:bg-gray-800 text-blue-600 dark:text-blue-400 px-3 py-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                              <span className="truncate max-w-[150px]">{att.name}</span>
                            </a>
                          )
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                </Fragment>
              );
            })}
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>
      
      {/* Message input */}
      <form ref={formRef} onSubmit={handleSendMessage} className="relative border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 flex-shrink-0 safe-area-bottom max-h-[40vh] overflow-y-auto">
        {/* Mention dropdown */}
        {showMentionList && filtered.length > 0 && (
          <div
            ref={mentionListRef}
            className="absolute bottom-full left-0 right-0 mx-4 mb-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-48 overflow-y-auto z-10"
          >
            {filtered.map((user, index) => (
              <button
                key={user.id}
                type="button"
                onClick={() => handleMentionSelect(user)}
                className={`w-full px-3 py-2 text-left flex items-center gap-3 ${
                  index === selectedMentionIndex
                    ? 'bg-blue-50 dark:bg-blue-900/30'
                    : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                <div className={`w-8 h-8 ${getAvatarColor(user.name)} rounded-full flex items-center justify-center text-white text-xs font-medium`}>
                  {user.name.charAt(0)}
                </div>
                <div className="flex-1 min-w-0">
                  <span className="text-sm font-medium text-gray-900 dark:text-white">{user.name}</span>
                  <span className="text-xs text-gray-500 ml-2">{user.email}</span>
                </div>
              </button>
            ))}
          </div>
        )}
        
        {/* Reply preview */}
        {replyingTo && (
          <div className="px-2 pt-2 pb-0 overflow-hidden">
            <div className="flex items-center gap-2 bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 rounded-r-lg px-3 py-1.5 overflow-hidden">
              <svg className="w-4 h-4 text-blue-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
              </svg>
              <div className="flex-1 min-w-0 overflow-hidden">
                <p className="text-xs font-medium text-blue-600 dark:text-blue-400 truncate">{replyingTo.senderName} に返信</p>
                <p className="text-sm text-gray-600 dark:text-gray-300 truncate">{replyingTo.content}</p>
              </div>
              <button
                type="button"
                onClick={() => setReplyingTo(null)}
                className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 flex-shrink-0"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        )}
        
        {/* Pending attachments preview */}
        {pendingAttachments.length > 0 && (
          <div className="px-2 pt-2 pb-0 flex flex-wrap gap-2">
            {pendingAttachments.map((att, index) => (
              <div
                key={index}
                className="relative group bg-gray-100 dark:bg-gray-800 rounded-lg p-2 flex items-center gap-2"
              >
                {att.previewUrl ? (
                  <img
                    src={att.previewUrl}
                    alt={att.file.name}
                    className="w-12 h-12 object-cover rounded"
                  />
                ) : (
                  <div className="w-12 h-12 bg-gray-200 dark:bg-gray-700 rounded flex items-center justify-center">
                    <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                )}
                <div className="flex-1 min-w-0 max-w-[150px]">
                  <p className="text-xs text-gray-700 dark:text-gray-300 truncate">{att.file.name}</p>
                  <p className="text-[10px] text-gray-400">
                    {(att.file.size / 1024).toFixed(1)} KB
                    {att.uploading && ' - アップロード中...'}
                    {att.error && <span className="text-red-500"> - {att.error}</span>}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => handleRemoveAttachment(index)}
                  disabled={att.uploading}
                  className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-xs hover:bg-red-600 disabled:opacity-50"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
        
        {/* Input area */}
        <div className="flex items-center gap-2 px-2 py-2">
          {/* File attachment button */}
          {!hideFileUpload && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={handleFileSelect}
                className="hidden"
                accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.zip"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={sending}
                className="w-9 h-9 flex items-center justify-center text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors disabled:opacity-50 flex-shrink-0"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                </svg>
              </button>
            </>
          )}
          
          <div className="flex-1 relative">
            <textarea
              ref={textareaRef}
              value={newMessage}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              placeholder="メッセージを入力"
              rows={1}
              maxLength={5000}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-3xl bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white text-sm resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:outline-none focus:bg-white dark:focus:bg-gray-700 max-h-24"
              style={{ minHeight: '40px' }}
            />
          </div>
          <button
            type="submit"
            disabled={sending || (!newMessage.trim() && pendingAttachments.length === 0)}
            className="w-9 h-9 flex items-center justify-center text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 disabled:text-gray-300 dark:disabled:text-gray-600 rounded-full transition-colors disabled:cursor-not-allowed flex-shrink-0"
          >
            {sending ? (
              <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
            ) : (
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
              </svg>
            )}
          </button>
        </div>
      </form>
      
      {/* Mention tooltip */}
      {mentionTooltip && mentionTooltip.user && (
        <div
          className="fixed bg-gray-900 text-white text-xs rounded-lg px-3 py-2 shadow-lg z-50"
          style={{
            left: mentionTooltip.x,
            top: mentionTooltip.y,
          }}
        >
          <p className="font-medium">{mentionTooltip.user.name}</p>
          <p className="text-gray-400">{mentionTooltip.user.email}</p>
        </div>
      )}
      
      {/* 画像プレビューポップアップ */}
      {previewImage && (
        <div
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => setPreviewImage(null)}
        >
          <div className="relative max-w-full max-h-full" onClick={e => e.stopPropagation()}>
            {/* 閉じるボタン */}
            <button
              type="button"
              onClick={() => setPreviewImage(null)}
              className="absolute -top-10 right-0 text-white hover:text-gray-300 transition-colors"
            >
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            
            {/* 画像 */}
            <img
              src={previewImage.url}
              alt={previewImage.name}
              className="max-w-full max-h-[80vh] object-contain rounded-lg shadow-2xl"
            />
            
            {/* ファイル名とダウンロードボタン */}
            <div className="absolute -bottom-12 left-0 right-0 flex items-center justify-center gap-4">
              <span className="text-white text-sm truncate max-w-[300px]">{previewImage.name}</span>
              <a
                href={previewImage.url}
                download={previewImage.name}
                className="inline-flex items-center gap-1.5 text-sm text-white bg-blue-600 hover:bg-blue-700 px-3 py-1.5 rounded-lg transition-colors"
                onClick={e => e.stopPropagation()}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                ダウンロード
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
